'use client'

import { Box, Paper, Typography, Chip, Button } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Trans } from '@lingui/react/macro'
import type { EthicsFormSubmission } from '../model/types'
import { getStatusConfig, isResponseVisible } from '../lib/statusConfig'
import { getFieldDisplayValue } from '../lib/helpers'

type SubmissionCardProps = {
  submission: EthicsFormSubmission
  onViewDetail: () => void
}

export function SubmissionCard({ submission, onViewDetail }: SubmissionCardProps) {
  const config = getStatusConfig(submission.status)
  const hasResponse = !!submission.ethicsResponse && isResponseVisible(submission.status)
  const descriptionResp = submission.responses.find((r) => r.fieldId === 'description')
  const descriptionValue = descriptionResp ? getFieldDisplayValue(descriptionResp.value) : ''

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid transparent',
        boxShadow: '0 12px 40px rgba(25,28,29,0.06)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        '&:hover': { borderColor: 'divider' },
        transition: 'border-color 0.2s',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.12em', color: 'primary.main', lineHeight: 1.5 }}>
          {submission.template.title}
        </Typography>
        <Chip label={config.label} color={config.muiColor} size="small" sx={{ fontWeight: 700, fontSize: '0.68rem', borderRadius: 4, height: 24 }} />
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}>
        {descriptionValue}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {hasResponse ? (
            <>
              <Trans>Response received on</Trans> {new Date(submission.respondedAt || submission.updatedAt).toLocaleDateString()}
            </>
          ) : (
            <>
              <Trans>Submitted on</Trans> {new Date(submission.submittedAt || submission.createdAt).toLocaleDateString()}
            </>
          )}
        </Typography>
        <Button
          size="small"
          endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
          onClick={onViewDetail}
          sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'info.main' }}
        >
          <Trans>View details</Trans>
        </Button>
      </Box>
    </Paper>
  )
}
